import { useEffect, useRef, useState, useCallback } from 'react';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

function getToken() {
  return localStorage.getItem('token') || null;
}

function soportado() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Devuelve { permiso, cargando, activarPush }
// - permiso: 'default' | 'granted' | 'denied'
// - activarPush(): llama al suscribirse — invocar desde un click del usuario
export default function usePushNotifications() {
  const [permiso, setPermiso] = useState(() =>
    soportado() ? Notification.permission : 'unsupported'
  );
  const [cargando, setCargando] = useState(false);
  const intentado = useRef(false);

  // Si ya tiene permiso 'granted', suscribir silenciosamente en cada carga
  // (el backend usa upsert, así que no hay problema si ya existe)
  useEffect(() => {
    if (!soportado()) return;
    if (intentado.current) return;
    if (Notification.permission !== 'granted') return;
    if (!getToken()) return;
    intentado.current = true;
    suscribirSilencioso();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function suscribirSilencioso() {
    try {
      await registrarSuscripcion();
    } catch (err) { console.warn('suscribirSilencioso:', err.message); }
  }

  async function registrarSuscripcion() {
    const token = getToken();
    if (!token) throw new Error('Sin token');

    const res = await fetch('/api/web-push/vapid-public-key');
    if (!res.ok) throw new Error('No se pudo obtener VAPID key');
    const { publicKey } = await res.json();

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    const saveRes = await fetch('/api/web-push/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(subscription),
    });
    if (!saveRes.ok) {
      const err = await saveRes.text();
      throw new Error(`Backend rechazó la suscripción: ${saveRes.status} ${err}`);
    }

    sessionStorage.setItem('push_subscribed', '1');
    setPermiso('granted');
  }

  // Llamar desde el click de un botón para pedir permiso y suscribir
  const activarPush = useCallback(async () => {
    if (!soportado()) {
      alert(`❌ Tu navegador no soporta notificaciones push.\n\nEn iOS debes instalar la app en el inicio (añadir a pantalla de inicio) y abrirla desde el ícono.`);
      return;
    }
    if (location.protocol !== 'https:') {
      alert('❌ Se requiere HTTPS para notificaciones push.');
      return;
    }
    if (cargando) return;

    setCargando(true);
    try {
      const resultado = await Notification.requestPermission();
      setPermiso(resultado);
      if (resultado === 'granted') {
        await registrarSuscripcion();
        alert('✅ ¡Notificaciones activadas! Recibirás alertas de nuevos incidentes.');
      } else if (resultado === 'denied') {
        alert('🔕 Notificaciones bloqueadas.\n\nPara activarlas:\n1. Toca el 🔒 candado en la barra de URL\n2. Notificaciones → Permitir\n3. Recarga la página');
      } else {
        alert('⚠️ Permiso no concedido. Intenta de nuevo.');
      }
    } catch (err) {
      alert('❌ Error: ' + err.message);
      console.error('activarPush:', err);
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargando]);

  return { permiso, cargando, activarPush };
}

